import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UnsavedChangesValues } from 'common/types/unsavedChanges';
import { UnsavedChangesContext } from 'context';
import { FundInvestmentsParams } from 'pages/FundInvestments';
import {
  DeletedMarketAdjustments,
  DeletedMarketComparisons,
  MarketAdjustment,
  MarketComparisons,
} from 'pages/FundInvestments/pages/FundInvestmentMarketAdjustment';
import { AreAllCellsValid, UseFundInvestmentSpreadsheetReturn } from './useFundInvestmentSpreadsheet.types';

const useFundInvestmentSpreadsheet = (): UseFundInvestmentSpreadsheetReturn => {
  const { companySlugParam: fundInvestmentSlugParam = '', tableSlugParam: fundInvestmentTableSlugParam = '' } =
    useParams<FundInvestmentsParams>();

  const { cleanAction: cleanUnsavedChanges, setAction: setUnsavedChanges } = useContext(
    UnsavedChangesContext
  ) as UnsavedChangesValues;

  // Errors and validations
  const [areAllCellsValid, setAreAllCellsValid] = useState<AreAllCellsValid | null>(null);
  const [arePendingChanges, setArePendingChanges] = useState<boolean>(false);
  const [hasInvalidFundSelections, setHasInvalidFundSelections] = useState<boolean>(false);
  const [hasValidationErrors, setHasValidationErrors] = useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(false);

  // As of date
  const [currentAsOfDate, setCurrentAsOfDate] = useState<string | null>(null);

  // Market adjustment and comparisons
  const [currentMarketAdjustment, setCurrentMarketAdjustment] = useState<MarketAdjustment>(null);
  const [currentMarketComparisons, setCurrentMarketComparisons] = useState<MarketComparisons>(null);

  // Deleted adjustments and comparisons
  const [currentDeletedMarketAdjustments, setCurrentDeletedMarketAdjustments] =
    useState<DeletedMarketAdjustments>(null);
  const [currentDeletedMarketComparisons, setCurrentDeletedMarketComparisons] =
    useState<DeletedMarketComparisons>(null);

  // Reset values when changing page
  useEffect(() => {
    // Errors and validations
    setAreAllCellsValid(null);
    setArePendingChanges(false);
    setHasInvalidFundSelections(false);
    setHasValidationErrors(false);
    setIsValidating(false);

    // Market adjustment
    setCurrentDeletedMarketAdjustments(null);
    setCurrentDeletedMarketComparisons(null);
  }, [fundInvestmentSlugParam, fundInvestmentTableSlugParam]);

  return {
    areAllCellsValid,
    arePendingChanges,
    cleanUnsavedChanges,
    currentAsOfDate,
    currentDeletedMarketAdjustments,
    currentDeletedMarketComparisons,
    currentMarketAdjustment,
    currentMarketComparisons,
    hasInvalidFundSelections,
    hasValidationErrors,
    isValidating,
    setAreAllCellsValid,
    setArePendingChanges,
    setCurrentAsOfDate,
    setCurrentDeletedMarketAdjustments,
    setCurrentDeletedMarketComparisons,
    setCurrentMarketAdjustment,
    setCurrentMarketComparisons,
    setHasInvalidFundSelections,
    setHasValidationErrors,
    setIsValidating,
    setUnsavedChanges,
  };
};

export default useFundInvestmentSpreadsheet;
